import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { useStores } from '../../hooks/use-stores';
import ErrorMessage from '../../components/errormessage';
import CircularProgress from '@mui/material/CircularProgress';
import WebhookFormDef from '../../forms/webhook';
import {
    Block,
    FlexDiv,
    TextDefault,
    BigModal,
    Heading2,
    ThemedButton,
    DarkArrowTooltipIcon,
    TextTiny,
    Tag,
} from '@awarego/awarego-components';
import TableClient from '../../components/table/TableClient';
import RequireRole from '../RequireRole';
import SimpleForm from '../../components/forms/SimpleForm';
import Box from '@mui/material/Box';
import Modal from 'react-modal';

function WebHooks() {
    const { commonStore, companyStore, store, brandingStore } = useStores();
    const { currentCompany } = store;
    const { webhooks, error, deletingWebhooks, loadingWebhooks } = companyStore;
    const rowsPerPage = 5;
    useEffect(() => {
        Modal.setAppElement('body');
    }, []);

    useEffect(() => {
        if (currentCompany)
            companyStore.loadWebhooks(currentCompany.company_id);
    }, [currentCompany]);

    const createNew = () => {
        localStore.webhookForm.clear();
        localStore.webhookForm.resetValidation();
        openModal({ id: -1, name: '', url: '', status: 1, definition: {} });
    };

    const openModal = (webhook) => {
        localStore.webhookForm.clear();
        localStore.webhookForm.update({
            ...webhook,
            enabled: webhook.status === 1,
            type: webhook.definition.eventName,
            signatureSecret: webhook.definition.signatureSecret,
        });
        localStore.webhookForm.resetValidation();
        localStore.setModalId(webhook.id);
        localStore.setModalIsOpen(true);
    };

    const deleteKey = (id) => {
        commonStore.showConfirm(
            'Do you really want to delete this Webhook?',
            'Delete',
            'Delete Webhook?',
            () => {
                companyStore.deleteWebhook(id, {
                    companyId: currentCompany.company_id,
                });
            }
        );
    };

    const columns = useMemo(() => {
        return [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: observer((x) => {
                    return (
                        <>
                            {x.value && x.value.length > 0 ? (
                                x.value
                            ) : (
                                <Tag text="No Name" />
                            )}{' '}
                        </>
                    );
                }),
            },
            {
                Header: 'Url',
                accessor: 'url',
                Cell: observer((x) => {
                    return <TextTiny>{x.value}</TextTiny>;
                }),
                disableGlobalFilter: true,
            },
            {
                Header: '',
                accessor: 'id',
                Cell: observer((x) => (
                    <FlexDiv>
                        {deletingWebhooks.includes(x.value) ? (
                            <CircularProgress />
                        ) : (
                            buildExtraActionsColumn(x.row.original)
                        )}
                    </FlexDiv>
                )),
                disableGlobalFilter: true,
            },
        ];
    }, [
        commonStore,
        companyStore,
        currentCompany.company_id,
        deletingWebhooks,
    ]);

    const buildExtraActionsColumn = (row) => {
        return (
            <>
                <RequireRole>
                    <DarkArrowTooltipIcon
                        customFunction={() => {
                            openModal(row);
                        }}
                        row={row}
                        title="Edit name"
                        icon={'edit'}
                    ></DarkArrowTooltipIcon>
                    <DarkArrowTooltipIcon
                        customFunction={() => deleteKey(row.id)}
                        row={row}
                        title="Delete API Key"
                        icon={'delete'}
                    ></DarkArrowTooltipIcon>
                </RequireRole>
            </>
        );
    };

    const localStore = useLocalObservable(() => ({
        modalId: -1,
        isModalOpen: null,
        setModalId(id) {
            this.modalId = id;
        },
        setModalIsOpen(isOpen) {
            this.isModalOpen = isOpen;
        },
        webhookForm: new WebhookFormDef(
            {},
            {
                hooks: {
                    onSuccess() {
                        if (localStore.modalId === -1) {
                            companyStore.createWebhook(
                                currentCompany.company_id,
                                localStore.webhookForm.values()
                            );
                        } else {
                            companyStore.updateWebhook(
                                currentCompany.company_id,
                                localStore.modalId,
                                localStore.webhookForm.values()
                            );
                        }

                        localStore.setModalIsOpen(false);
                    },
                },
            }
        ),
    }));

    return (
        <FlexDiv>
            <Block flexGrow="9" mr="20">
                <ErrorMessage error={error} />
                <FlexDiv column>
                    <Heading2 mb={8}>Webhooks</Heading2>
                    <TextDefault>
                        This Webhooks is designed to receive notifications on
                        various actions and modifications of trainings and
                        assessments.
                    </TextDefault>
                </FlexDiv>
                <FlexDiv pt="32" pb="32">
                    <ThemedButton
                        onClick={createNew}
                        mode="primary"
                        text="Create Webhook"
                    />
                </FlexDiv>
                {loadingWebhooks && <CircularProgress />}
                <TableClient
                    columns={columns}
                    initiallyLoaded={loadingWebhooks === false}
                    data={[...webhooks]}
                    noItemFoundText={'No existing keys'}
                    hidePagination={webhooks.length <= rowsPerPage}
                    overflowInitial={true}
                />
            </Block>

            <BigModal
                ariaHideApp={false}
                isOpen={localStore.isModalOpen}
                width={600}
                onRequestClose={() => localStore.setModalIsOpen(false)}
                heading={
                    localStore.modalId === -1
                        ? 'Create Webhook'
                        : 'Edit Webhook'
                }
                modalContent={
                    <Box minHeight={450}>
                        <SimpleForm
                            form={localStore.webhookForm}
                            fullWidth={true}
                        />
                    </Box>
                }
                modalFooterContent={
                    <Fragment>
                        <ThemedButton
                            mode="primary"
                            text={localStore.modalId === -1 ? 'Create' : 'Save'}
                            onClick={async (e) => {
                                localStore.webhookForm.submit(e);
                            }}
                        />
                        <ThemedButton
                            mode="secondary"
                            onClick={() => {
                                localStore.setModalIsOpen(false);
                            }}
                            text="Close"
                        />
                    </Fragment>
                }
            />
        </FlexDiv>
    );
}

export default observer(WebHooks);
